import {useState} from 'react';
import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import {
  type ChannelState,
  NotificationPreferenceList,
} from '@common-web/components/notification-preference-list';
import {type NotificationChannel, type UserNotificationType} from '@common/types/notifications';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@common-web/components/ui/card';
import {toast} from '@common-web/hooks/use-toast';
import {Bell, Loader2} from 'lucide-react';

export function NotificationSettings({showTitle = true}: {showTitle?: boolean}) {
  const [updatingType, setUpdatingType] = useState<UserNotificationType | null>(null);

  const availableQuery = trpc.user.notificationSettings.getAvailableTypes.useQuery();
  const utils = trpc.useUtils();

  const updateMutation = trpc.user.notificationSettings.updateSetting.useMutation({
    onSuccess: () => {
      toast({
        title: 'Settings updated',
        description: 'Your notification preferences have been saved',
      });
      void utils.user.notificationSettings.getAvailableTypes.invalidate();
    },
    onError: createMutationErrorHandler('Could not update notification setting'),
    onSettled: () => setUpdatingType(null),
  });

  const events = availableQuery.data ?? [];

  const getChannelState = (type: UserNotificationType): ChannelState => {
    const e = events.find((ev) => ev.key === type);
    return {email: e?.emailEnabled ?? false, inApp: e?.inAppEnabled ?? false};
  };

  const handleToggle = (
    type: UserNotificationType,
    channel: NotificationChannel,
    value: boolean,
  ) => {
    const current = getChannelState(type);
    setUpdatingType(type);
    updateMutation.mutate({
      type,
      emailEnabled: channel === 'email' ? value : current.email,
      inAppEnabled: channel === 'inApp' ? value : current.inApp,
    });
  };

  return (
    <Card>
      {showTitle && (
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Bell className='h-5 w-5' />
            Notification Settings
          </CardTitle>
          <CardDescription>
            Choose how you want to be notified about important events and updates
          </CardDescription>
        </CardHeader>
      )}
      <CardContent>
        {availableQuery.isLoading ? (
          <div className='flex items-center justify-center p-8'>
            <Loader2 className='h-6 w-6 animate-spin' />
            <span className='ml-2'>Loading notification settings...</span>
          </div>
        ) : availableQuery.error ? (
          <div className='text-sm text-destructive'>
            Failed to load notification settings: {availableQuery.error.message}
          </div>
        ) : (
          <NotificationPreferenceList
            events={events}
            getChannelState={getChannelState}
            onToggle={handleToggle}
            updatingType={updatingType}
            emptyState='No notification settings are available to configure at this time.'
          />
        )}
      </CardContent>
    </Card>
  );
}
